import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { LoginForm } from '@/types'
import { Loader2 } from 'lucide-react'
import { Navbar } from '@/components/layout/Navbar'
import { auth } from '@/config/firebase'

// Custom styles mapping to user request
const displayFont = { fontFamily: '"Inter", sans-serif' }

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, isFounder } = useAuth()
  const { toast } = useToast()

  // Show welcome message if coming from registration
  useEffect(() => {
    if (location.state?.registrationComplete) {
      toast({
        title: 'Registration successful!',
        description: 'Please sign in with Google to access your account.',
        variant: 'success',
      })
      window.history.replaceState({}, document.title)
    }
  }, [location.state, toast])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      await signIn(data.email, data.password)

      // Check if email is verified
      const currentUser = auth.currentUser
      if (currentUser && !currentUser.emailVerified) {
        toast({
          title: 'Email not verified',
          description: 'Please verify your email before accessing your account.',
          variant: 'destructive',
        })
        navigate('/auth/verify-email', { state: { email: currentUser.email }, replace: true })
        return
      }

      toast({
        title: 'Welcome back!',
        description: 'You have been successfully logged in.',
        variant: 'success',
      })

      if (isFounder) {
        navigate('/', { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }

    } catch (error) {
      toast({
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'Invalid credentials',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-unicorn-mesh-light dark:bg-none dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden antialiased transition-colors duration-300 min-h-screen relative flex flex-col" style={displayFont}>
      <style>{`
        .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        .filled-icon { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .dark ::-webkit-scrollbar-thumb { background: #334155; }
        .dark ::-webkit-scrollbar-thumb:hover { background: #475569; }
        .gradient-text {
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
            background-image: linear-gradient(to right, #3B52D0, #4194F2, #A378CF, #F0766E);
        }
        .glass-card {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
        }
        .dark .glass-card {
            background: rgba(15, 23, 42, 0.6);
        }
      `}</style>

      <div className="fixed inset-0 z-[-1] opacity-0 dark:opacity-40 bg-unicorn-mesh pointer-events-none transition-opacity duration-500"></div>

      <Navbar variant="auth" />

      <main className="flex-grow flex items-center justify-center relative py-12 md:py-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 pointer-events-none opacity-60 dark:opacity-30">
          <div className="absolute top-[5%] left-[15%] w-[500px] h-[500px] bg-[#3B52D0]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
          <div className="absolute bottom-[10%] right-[15%] w-[400px] h-[400px] bg-[#F0766E]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-[40%] right-[30%] w-[300px] h-[300px] bg-[#A378CF]/10 rounded-full blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="w-full max-w-md px-6 relative z-10">
          <div className="glass-card rounded-2xl border border-white/50 dark:border-slate-700/50 p-8 shadow-2xl backdrop-blur-xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#3B52D0]/10 to-[#F0766E]/10 mb-4">
                <span className="material-symbols-outlined text-[#4f46e5] text-2xl">lock_open</span>
              </div>
              <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">Welcome Back</h1>
              <p className="text-slate-600 dark:text-slate-400">Sign in to access your analyst dashboard</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" htmlFor="email">Email address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="material-symbols-outlined text-slate-400 text-[20px]">mail</span>
                  </div>
                  <input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    className={`w-full h-11 pl-10 pr-4 rounded-lg border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#4f46e5] focus:ring-[#4f46e5]/20 transition-colors placeholder:text-slate-400 ${errors.email ? 'border-red-500' : ''}`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email address',
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="password">Password</label>
                  <Link className="text-xs font-semibold text-[#4f46e5] hover:text-[#9333ea] transition-colors" to="/auth/forgot-password">Forgot password?</Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="material-symbols-outlined text-slate-400 text-[20px]">lock</span>
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className={`w-full h-11 pl-10 pr-10 rounded-lg border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#4f46e5] focus:ring-[#4f46e5]/20 transition-colors placeholder:text-slate-400 ${errors.password ? 'border-red-500' : ''}`}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <span className="material-symbols-outlined text-[20px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#4f46e5] focus:ring-[#4f46e5]/20 border-gray-300 rounded bg-white/50 dark:bg-slate-900/50"
                />
                <label className="ml-2 block text-sm text-slate-600 dark:text-slate-400" htmlFor="remember-me">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-logo-gradient hover:shadow-lg hover:shadow-purple-500/30 text-white h-11 rounded-lg font-bold transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <span>Sign In</span>
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center text-sm text-slate-600 dark:text-slate-400">
              Don't have an account?
              <Link className="font-bold text-slate-900 dark:text-white hover:text-[#4f46e5] transition-colors ml-1" to="/register">Sign up for free</Link>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Protected by reCAPTCHA and subject to the
              <Link className="underline hover:text-slate-500 dark:hover:text-slate-300 ml-1" to="/privacy-policy">Privacy Policy</Link> and
              <Link className="underline hover:text-slate-500 dark:hover:text-slate-300 ml-1" to="/terms-of-service">Terms of Service</Link>.
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-white/50 dark:bg-slate-900 border-t border-indigo-100 dark:border-slate-800 py-8 backdrop-blur-sm mt-auto">
        <div className="max-w-[1280px] mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4 text-slate-400 dark:text-slate-600">
            <img alt="uniscout Logo" className="h-6 w-auto opacity-50 grayscale" src="/logo.png" />
          </div>
          <div className="flex justify-center gap-6 mb-4 flex-wrap text-sm">
            <a className="text-slate-500 hover:text-[#4f46e5] transition-colors" href="#">Contact Support</a>
            <a className="text-slate-500 hover:text-[#4f46e5] transition-colors" href="#">System Status</a>
          </div>
          <p className="text-slate-400 dark:text-slate-600 text-xs">© 2024 uniscout.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
